import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import { CircularProgress, Divider } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Bugsnag from '@bugsnag/js';
import _ from 'lodash';

import {
  setSelectedRestaurant as storeSelectedRestaurant,
  getRestaurantDetailsRequest,
  getRestaurantMenuRequest,
} from '../../../redux/legacy/reducers/ducks/legacy/RestaurantsDuck';
import { getOrdersHistory } from '../../../redux/legacy/reducers/ducks/legacy/ProfileDuck';
import { toggleGlobalLoaderQueue } from '../../../redux/legacy/reducers/ducks/legacy/MainDuck';
import CartForm from '../../input/CartForm/CartForm';
import FilterMobile from '../../layout/FilterMobile';
import AddToCartPanel from '../../layout/AddToCartPanel';
import RestaurantMenu from '../../layout/RestaurantMenu';
import RestaurantHeader from '../../layout/RestaurantHeader';
import useDesktopCheck from '../../../util/hooks/UseDesktopCheck';
import useScrollRestoration from '../../../hooks/legacy/useScrollRestoration';
import * as orderActions from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import { useRestaurant } from '../../../hooks/useRestaurant';
import Notification from '../../layout/Notification';
import {
  isConsumerApp,
  getImage,
  isRestaurantApp,
} from '../../../util/Helpers';
import getOrderFilterOptions from '../../../util/GetOrderFilterOptions';
import { getConfig } from '../../../AppConfig';
// import errorMessages from '../../../constants/errorMessages';
import useSeo from '../../../hooks/useSeo';
import { useWhen } from '../../../hooks/useWhen';

const isConsumerWeb = isConsumerApp();
const config = getConfig();
const isRestaurant = isRestaurantApp();

function Restaurant() {
  useScrollRestoration();
  const dispatch = useDispatch();
  const isDesktop = useDesktopCheck();
  const { setSelectedRestaurant, resetSelectedRestaurantMenu } =
    useRestaurant();
  const [isLoadingMenu, setIsLoadingMenu] = useState(false);
  const {
    setPageTitle,
    setJsonItem,
    setPageDescription,
    setPageKeywords,
    setCanonicalURL,
  } = useSeo();

  // Use useParams directly to get id
  const { id: originalId } = useParams();

  // Initialize id based on your conditions
  let id;

  if (isConsumerWeb) {
    // Change 'id' based on the condition
    id = originalId;
  } else {
    // Change 'id' based on the condition
    id = config?.profileKey;
  }

  const {
    date,
    restaurants,
    selectedRestaurant,
    checkoutData,
    restaurantDetails,
    saveFavoriteError,
    time,
    type,
    address,
    restaurantMenu,
  } = useSelector(
    ({
      order,
      profile,
      restaurant,
      address: selectedAddress,
      time: selectedTime,
      restaurants: restaurantsData,
    }) => ({
      date: selectedTime?.selectedDate,
      selectedRestaurant: restaurant?.selectedRestaurantId,
      checkoutData: order.checkoutData,
      restaurantDetails: restaurant?.selectedRestaurantData,
      restaurantMenu: restaurant?.selectedRestaurantMenu,
      saveFavoriteError: profile.saveFavoriteError,
      type: order?.type,
      time: selectedTime?.selectedTime,
      address: selectedAddress?.selectedAddress,
      restaurants: restaurantsData.restaurants,
    })
  );

  useEffect(() => {
    dispatch(getRestaurantDetailsRequest(id));
  }, [id]);

  const { setSelectedTime } = useWhen();
  const selectedTime = useMemo(
    () => time || { label: 'ASAP', type: 'asap', value: 'asap' },
    [time]
  );
  const selectedType = useMemo(() => type, [type]);
  const selectedAddress = useMemo(() => address, [address]);

  useEffect(() => {
    if (!time) {
      setSelectedTime(selectedTime);
    }

    dispatch(
      toggleGlobalLoaderQueue({
        name: 'searchRestaurants',
        status: restaurantDetails === null,
      })
    );

    const websiteIcon = isRestaurant
      ? getImage(config, 'favicon', null)
      : getImage(config, 'consumer_favicon', null);

    if (restaurantDetails) {
      setPageTitle(isConsumerApp ? restaurantDetails.name : 'Menu');

      const websiteBanner = restaurantDetails?.media?.site_logo ?? '';
      const websiteKeywords =
        restaurantDetails?.tags
          .map((tag) => tag.name.toLowerCase())
          .join(', ') ?? '';

      const altName = restaurantDetails?.name?.split(' - ')[0] || '';

      // Set canonical URL for restaurant page to prevent duplicate content
      const restaurantCanonicalUrl = `${window.location.origin}/restaurant/${id}`;
      const domain = new URL(restaurantCanonicalUrl).hostname;
      setCanonicalURL(restaurantCanonicalUrl);

      const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Restaurant',
        name: restaurantDetails?.name,
        alternateName: [altName, domain],
        description: restaurantDetails?.description,
        keywords: websiteKeywords,
        telephone: restaurantDetails?.phone,
        url: `${window.location.origin}`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': restaurantCanonicalUrl,
        },
        address: {
          '@type': 'PostalAddress',
          streetAddress: restaurantDetails?.address,
          addressLocality: restaurantDetails?.city,
          addressRegion: restaurantDetails?.state,
          postalCode: restaurantDetails?.zipcode,
          addressCountry: 'US',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: restaurantDetails?.latitude,
          longitude: restaurantDetails?.longitude,
        },
        menu: `${window.location.origin}/menu`,
        servesCuisine:
          _.startCase(restaurantDetails?.tags[0]?.name) || 'American',
        image: [`${window.location.protocol}${websiteBanner}`],
        logo: websiteIcon?.startsWith('//')
          ? `https:${websiteIcon}`
          : websiteIcon,
        publicAccess: true,
      };

      setJsonItem(jsonLd);
      setPageDescription(restaurantDetails?.description);
      setPageKeywords(websiteKeywords);
    }
  }, [restaurantDetails]);

  const [givenTimeOptions, setTimeOptions] = useState([]);

  const dateTimeOptions = useMemo(() => {
    const todayDayNumber = new Date().getDate();
    const key = date?.fullName === 'Today' ? todayDayNumber : date?.key;
    return givenTimeOptions?.[key]?.[type.value] ?? [];
  }, [givenTimeOptions, date, type]);

  useEffect(() => {
    if (restaurantDetails && id === selectedRestaurant) {
      const { timeOptions } = getOrderFilterOptions(restaurantDetails);
      setTimeOptions(timeOptions);
    }
  }, [selectedRestaurant, restaurantDetails, id]);

  useEffect(() => {
    if (
      selectedType &&
      selectedTime &&
      selectedRestaurant &&
      dateTimeOptions?.length >= 1
    ) {
      setIsLoadingMenu(true);
      resetSelectedRestaurantMenu();
      if (
        !(selectedTime?.value === 'asap' || !selectedTime?.value) ||
        restaurants?.[selectedRestaurant].open
      ) {
        dispatch(
          getRestaurantMenuRequest({
            asapDate: null,
          })
        );
      } else if (!restaurants) {
        dispatch(
          getRestaurantMenuRequest({
            asapDate: null,
          })
        );
      }
      dispatch(
        getOrdersHistory({
          page: 1,
          queryData: '',
          callback: (e) => e,
          statusValue: null,
        })
      );
    } else if (
      selectedType &&
      selectedTime &&
      selectedRestaurant &&
      dateTimeOptions?.length === 1 &&
      dateTimeOptions[0]?.value === 'asap'
    ) {
      dispatch(
        getRestaurantMenuRequest({
          asapDate: null,
        })
      );
    }
  }, [
    selectedRestaurant,
    selectedType,
    selectedTime,
    selectedAddress,
    dateTimeOptions,
  ]);

  useEffect(() => {
    if (restaurantMenu) {
      setIsLoadingMenu(false);
      dispatch(orderActions.setOrderMinimums(restaurantMenu?.minimums));
    }
  }, [restaurantMenu]);

  useEffect(() => {
    // deprecated, use hook instead
    dispatch(storeSelectedRestaurant(id));
    // using the hook to update selected restaurant
    setSelectedRestaurant(id);
    if (
      (selectedRestaurant && selectedRestaurant !== id) ||
      (checkoutData && id !== checkoutData.restaurant)
    ) {
      dispatch(orderActions.resetOrder());
    }
  }, [id, dispatch, selectedRestaurant, checkoutData]);

  useEffect(() => {
    if (saveFavoriteError?.length) {
      Bugsnag.notify(saveFavoriteError);
      Notification.error(saveFavoriteError, {
        toastId: 'SaveFavoriteError',
      });
    }
  }, [saveFavoriteError]);

  const restaurantContent = () =>
    restaurantDetails ? (
      <Box sx={{ minHeight: '100vh' }}>
        <RestaurantHeader />
        <Box sx={{ minHeight: '100vh' }}>
          <RestaurantMenu isLoadingMenu={isLoadingMenu} />
        </Box>
        <AddToCartPanel />
      </Box>
    ) : (
      <CircularProgress />
    );

  const renderDesktop = () =>
    restaurantDetails ? (
      <main data-test-id="restaurant-page" style={{ marginBottom: '100px' }}>
        <Stack direction="row" justifyContent="space-between" spacing={4}>
          <Box sx={{ width: '65%' }}>{restaurantContent()}</Box>
          <Box sx={{ paddingTop: 1 }}>
            <Card
              sx={{
                height: 'fit-content',
                border: '1px solid lightgrey',
                px: 2.5,
                py: 3,
              }}
              data-test-id="resto-cart-card"
            >
              <Typography mb={1} variant="body1">
                Your Cart from
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {restaurantDetails.name}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, mt: 2, mb: 1 }}
              >
                Your Items
              </Typography>
              <Divider />
              {isLoadingMenu ? (
                <Skeleton width="100%" height={100} />
              ) : (
                <CartForm isDesktop />
              )}
            </Card>
          </Box>
        </Stack>
      </main>
    ) : (
      <Box sx={{ minHeight: '100vh' }} />
    );

  const renderMobile = () => (
    <main data-test-id="restaurant-page" style={{ marginBottom: '100px' }}>
      <FilterMobile />
      {restaurantDetails && restaurantContent(restaurantDetails)}
    </main>
  );

  if (selectedRestaurant) {
    if (isDesktop) {
      return renderDesktop();
    }
    return renderMobile();
  }
  return <Box sx={{ minHeight: '100vh' }} />;
}

export default Restaurant;
